import { observer } from 'mobx-react-lite'
import { type CheckoutViewModel } from '@/features/jobs/drafting/checkout/CheckoutViewModel'
import {
  type JobCheckoutViewModel,
  type AppliedCouponViewModel,
} from '@/features/jobs/drafting/checkout/JobCheckoutViewModel'
import {
  Summary,
  SummaryCoupon,
  SummaryDiscount,
  SummaryJob,
  SummaryJobActions,
  type SummaryJobPaymentStatus,
} from '@/features/jobs/drafting/checkout/components/Checkout/Summary'
import { extractMessageFromError } from '@/utils/error-util'
import { formatMoney } from '@/utils/money-util'

export interface SummaryContainerProps {
  vm: CheckoutViewModel
}

export const SummaryContainer = observer(function SummaryContainer({
  vm,
}: SummaryContainerProps) {
  return (
    <Summary
      isCheckoutCompleted={vm.checkoutCompleted}
      total={formatMoney(vm.total)}
    >
      {vm.jobCheckoutVms.map((jobVm) => (
        <SummaryJobContainer key={jobVm.id} vm={jobVm} />
      ))}
    </Summary>
  )
})

interface SummaryJobContainerProps {
  vm: JobCheckoutViewModel
}

const SummaryJobContainer = observer(function SummaryJobContainer({
  vm,
}: SummaryJobContainerProps) {
  const paymentStatus = vm.pay.match({
    pending: (): SummaryJobPaymentStatus => ({ state: 'pending' }),
    rejected: (err): SummaryJobPaymentStatus => ({
      state: 'rejected',
      errorMessage: extractMessageFromError(err),
    }),
    resolved: (): SummaryJobPaymentStatus => ({ state: 'resolved' }),
  })

  return (
    <SummaryJob
      name={vm.job.displayName}
      actions={
        <SummaryJobActions
          canRemoveJob={vm.canRemoveJob}
          onRemoveJob={vm.remove}
          onViewJob={vm.viewJob}
        />
      }
      items={vm.jobScopeItems.map((item) => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
      }))}
      subtotal={vm.primaryAmount}
      discount={
        vm.canAddCoupons && (
          <SummaryDiscount
            hasCoupons={vm.hasCoupons}
            discountLabel={vm.discountLabel}
            coupon={
              vm.appliedCouponVm && (
                <SummaryCouponContainer vm={vm.appliedCouponVm} />
              )
            }
            onApplyCoupon={vm.openCouponDialog}
          />
        )
      }
      paymentStatus={paymentStatus}
      isCheckoutCompleted={vm.checkoutCompleted}
      isSpacious={vm.checkoutCompleted || !vm.canAddCoupons}
    />
  )
})

interface SummaryCouponContainerProps {
  vm: AppliedCouponViewModel
}

const SummaryCouponContainer = observer(function SummaryCouponContainer({
  vm,
}: SummaryCouponContainerProps) {
  return (
    <SummaryCoupon
      name={vm.name}
      description={vm.description}
      discount={vm.discount}
      onRemove={vm.removeCoupon}
    />
  )
})
